/**
 * 排出係数紐付け 判別コメントの組み立て / 分解 (spec spot-bidding.md §7)。
 *
 * 判別コメント = `[特定契約{会社情報 ID}{英字}]` (例: `[特定契約A0000A]`)。
 * JEPX 側がこの note を認識して特定契約由来の排出係数を紐付けるため、
 * 文字列はユーザー手入力させずシステム側で組み立てて `memo_jepx` に格納する
 * (AC-BSP-086)。 typo 防止が本機能の目的なので、 分解 (編集復元) は自社 ID
 * 完全一致時のみ認識する (= 他社 ID や自由文を誤認して checkbox が勝手に
 * ON になるのを防ぐ)。
 *
 * `memo_jepx` は判別コメント専用ではなく JEPX自由記述欄そのもの (AC-BSP-004) なので、
 * ユーザーが直接入力した自由記述と判別コメントが同じ note 内に同居しうる。
 * spec §7 機能詳細 4: 保存時は判別コメントを先頭に置き、 ユーザーの自由記述を
 * 後ろに連結する。 復元時は逆に、 先頭の判別コメント (あれば) を認識して
 * 切り離し、 残りを自由記述として返す (design `js/emission-linking.js` の
 * `splitNote` / `composeNote` と同じ規則)。
 */

/** JEPX 判別コメントの固定接頭辞 (= wire format リテラル、 翻訳対象外)。 */
const EMISSION_COMMENT_PREFIX = '[特定契約'
const EMISSION_COMMENT_SUFFIX = ']'

/** アルファベット入力の許容形 = 大文字半角英字 (A〜Z) 1 文字 (AC-BSP-085)。 */
const EMISSION_ALPHA_PATTERN = /^[A-Z]$/

/** 正規表現の特殊文字をエスケープする (retailerId を安全にパターン化するため)。 */
const escapeRegExp = (s: string): string =>
  s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

export type ParsedEmissionComment = {
  linked: boolean
  alpha: string
  /** 判別コメントを除いた残りの自由記述 (前後空白 trim 済み)。 */
  freeText: string
}

/** 判別コメント完成形を組み立てる (AC-BSP-084 プレビュー / AC-BSP-086 保存)。 */
export function composeEmissionComment(
  retailerId: string,
  alpha: string,
): string {
  return `${EMISSION_COMMENT_PREFIX}${retailerId}${alpha}${EMISSION_COMMENT_SUFFIX}`
}

/**
 * 判別コメント (あれば) + ユーザーの自由記述 を連結して保存用 note を組み立てる
 * (spec §7 機能詳細 4)。 判別コメントが無ければ自由記述のみ、 両方あれば半角
 * スペース区切りで連結する (design `composeNote` と同じ規則)。
 */
export function composeJepxNote(comment: string, freeText: string): string {
  const trimmedFree = freeText.trim()
  if (comment === '') return trimmedFree
  return trimmedFree !== '' ? `${comment} ${trimmedFree}` : comment
}

/**
 * 保存済み memo_jepx を排出係数紐付けの UI 状態 + 残りの自由記述に分解する
 * (編集復元)。 自社 ID (retailerId) と完全一致する完成形が**先頭**にある時のみ
 * linked=true にし、 それ以降を自由記述として切り出す。 認識できない場合は
 * 全体を自由記述として扱う (linked=false)。
 */
export function parseEmissionComment(
  memoJepx: string | undefined,
  retailerId: string | undefined,
): ParsedEmissionComment {
  const raw = memoJepx ?? ''
  if (retailerId !== undefined && retailerId !== '') {
    const pattern = new RegExp(
      `^\\s*${escapeRegExp(EMISSION_COMMENT_PREFIX)}${escapeRegExp(
        retailerId,
      )}([A-Z])${escapeRegExp(EMISSION_COMMENT_SUFFIX)}\\s*`,
    )
    const matched = pattern.exec(raw)
    if (matched !== null) {
      return {
        linked: true,
        alpha: matched[1],
        freeText: raw.slice(matched[0].length).trim(),
      }
    }
  }
  return { linked: false, alpha: '', freeText: raw.trim() }
}

/**
 * 先頭にある判別コメントを、会社情報 ID に関係なく取り除いた残りを返す (前後空白 trim 済み)。
 * 他の札の内容を新規の札へ写す場面 (複製) で、会社情報の取得失敗中や ID が変わった後でも
 * 判別コメントが自由記述として JEPX へ送られないようにする。
 */
export function stripEmissionComment(memoJepx: string | undefined): string {
  const pattern = new RegExp(
    `^\\s*${escapeRegExp(EMISSION_COMMENT_PREFIX)}[^${escapeRegExp(
      EMISSION_COMMENT_SUFFIX,
    )}]*${escapeRegExp(EMISSION_COMMENT_SUFFIX)}\\s*`,
  )
  return (memoJepx ?? '').replace(pattern, '').trim()
}

/** JEPX自由記述欄 (memo_jepx = eSLJ note) の物理上限 (AC-BSP-061)。 */
export const JEPX_NOTE_MAX_LEN = 70

/**
 * 判別コメントの後ろに置ける自由記述の最大文字数 (spec §7 機能詳細 4)。
 * 紐付け OFF (`linkedRetailerId` が undefined) なら note の上限そのまま、 ON なら
 * 判別コメント + 区切りの半角スペースの分を差し引く。 入力欄の `maxLength`・
 * 文字数カウンタ・保存時の超過エラーが同じ値を使う。
 */
export function jepxFreeTextMaxLength(
  linkedRetailerId: string | undefined,
): number {
  if (linkedRetailerId === undefined) return JEPX_NOTE_MAX_LEN
  // 英字は常に 1 文字なので、 未入力でも 1 文字分を確保する。
  const commentLength = composeEmissionComment(linkedRetailerId, 'A').length
  return Math.max(0, JEPX_NOTE_MAX_LEN - commentLength - 1)
}

export type JepxNoteResult =
  | { ok: true; note: string }
  | { ok: false; freeTextMaxLength: number }

/**
 * 画面の入力 (紐付け / 英字 / 自由記述) から保存用 note を組み立て、 上限超過なら
 * 自由記述の許容文字数を返す (spec §7 機能詳細 4。 超過検知の挙動は EN 判断範囲)。
 *
 * `saved` (編集中の札の保存済み値) を渡した場合、 入力が保存済み値の分解結果から
 * 変わっていなければ保存済みの文字列をそのまま返す。 CSV 取込の札のように
 * 判別コメントと自由記述の間に空白が無い / 全角空白の note を、 利用者が触って
 * いないのに書き換えない (= 区切りを半角スペースに直すと 1 文字増えて上限を
 * 超えることもある) ため。
 */
export function buildJepxNote(input: {
  /** 紐付け ON なら会社情報 ID、 OFF なら undefined。 */
  linkedRetailerId: string | undefined
  alpha: string
  freeText: string
  saved?: { memoJepx: string | undefined; retailerId: string | undefined }
}): JepxNoteResult {
  const { linkedRetailerId, alpha, freeText, saved } = input
  if (saved !== undefined) {
    const parsed = parseEmissionComment(saved.memoJepx, saved.retailerId)
    const linked = linkedRetailerId !== undefined
    const unchanged =
      linked === parsed.linked &&
      (!linked || alpha === parsed.alpha) &&
      freeText.trim() === parsed.freeText
    if (unchanged) return { ok: true, note: saved.memoJepx ?? '' }
  }
  const comment =
    linkedRetailerId !== undefined
      ? composeEmissionComment(linkedRetailerId, alpha)
      : ''
  const note = composeJepxNote(comment, freeText)
  if (note.length > JEPX_NOTE_MAX_LEN) {
    return {
      ok: false,
      freeTextMaxLength: jepxFreeTextMaxLength(linkedRetailerId),
    }
  }
  return { ok: true, note }
}

/** 大文字半角英字 (A〜Z) 1 文字か (AC-BSP-085)。 */
export function isValidEmissionAlpha(alpha: string): boolean {
  return EMISSION_ALPHA_PATTERN.test(alpha)
}
